import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Trophy,
  Award,
  RotateCcw,
  ArrowLeft,
  Loader2,
  Code,
  Target,
  Send
} from "lucide-react";

const TakeAssessment = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("intro"); // intro | taking | submitting | results
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [qStartTimes, setQStartTimes] = useState({});
  const [results, setResults] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchAssessment();
    return () => clearInterval(timerRef.current);
  }, [assessmentId]);

  const fetchAssessment = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/assessments/${assessmentId}`, {
        credentials: "include",
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        setAssessment(data);
        setTimeLeft(data.time_limit * 60);
      } else {
        toast.error("Assessment not found");
        navigate("/assessments");
      }
    } catch {
      toast.error("Failed to load assessment");
    } finally {
      setLoading(false);
    }
  };

  const startAssessment = () => {
    setPhase("taking");
    setStartTime(Date.now());
    setQStartTimes({ 0: Date.now() });
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const goToQuestion = (idx) => {
    setCurrentQ(idx);
    setQStartTimes((prev) => ({ ...prev, [idx]: Date.now() }));
  };

  const handleSubmit = useCallback(async () => {
    if (phase === "submitting" || phase === "results") return;
    clearInterval(timerRef.current);
    setPhase("submitting");

    const totalTime = Math.round((Date.now() - startTime) / 1000);
    const questions = assessment.questions || [];
    const submissionAnswers = questions.map((q) => ({
      question_id: q.question_id,
      answer: answers[q.question_id] || "",
      time_spent: Math.round(((qStartTimes[questions.indexOf(q)] || 0) - startTime) / 1000),
    }));

    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API}/assessments/${assessmentId}/submit`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ answers: submissionAnswers, total_time: totalTime }),
      });

      if (res.ok) {
        const data = await res.json();
        setResults(data);
        setPhase("results");
        if (data.passed) {
          toast.success("Congratulations! You passed!");
        } else {
          toast("Assessment complete. Review your results below.");
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit");
        setPhase("taking");
      }
    } catch {
      toast.error("Submission failed");
      setPhase("taking");
    }
  }, [phase, assessment, answers, startTime, qStartTimes, assessmentId]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-crimson" />
      </div>
    );
  }

  if (!assessment) return null;

  const questions = assessment.questions || [];
  const answeredCount = Object.keys(answers).length;

  // ─── INTRO PHASE ───
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-obsidian text-white">
        <div className="max-w-2xl mx-auto p-6 sm:p-10">
          <button onClick={() => navigate("/assessments")} className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 text-sm" data-testid="back-to-assessments">
            <ArrowLeft className="w-4 h-4" /> Back to Assessments
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-obsidian-light border border-[#222] rounded-lg p-6 sm:p-8 space-y-6">
            <div className="h-1.5 bg-gradient-to-r from-crimson to-crimson/30 rounded -mt-6 -mx-6 sm:-mt-8 sm:-mx-8 mb-6" />

            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="assessment-title">{assessment.title}</h1>
            <p className="text-gray-400">{assessment.description}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4">
              {[
                { icon: Clock, label: "Time Limit", value: `${assessment.time_limit} min` },
                { icon: Target, label: "Questions", value: questions.length },
                { icon: Trophy, label: "Pass Score", value: `${assessment.passing_score}%` },
                { icon: Award, label: "Difficulty", value: assessment.difficulty?.charAt(0).toUpperCase() + assessment.difficulty?.slice(1) },
              ].map((m) => (
                <div key={m.label} className="bg-obsidian border border-[#333] rounded p-3 text-center">
                  <m.icon className="w-5 h-5 text-crimson mx-auto mb-1" />
                  <p className="text-xs text-gray-500">{m.label}</p>
                  <p className="text-sm font-semibold">{m.value}</p>
                </div>
              ))}
            </div>

            {assessment.skill_tags?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Skills Tested</p>
                <div className="flex flex-wrap gap-2">
                  {assessment.skill_tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-crimson/10 border border-crimson/20 rounded text-xs text-crimson">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200/80 space-y-1">
                  <p>Once started, the timer cannot be paused.</p>
                  <p>Ensure you have a stable connection before beginning.</p>
                </div>
              </div>
            </div>

            <Button onClick={startAssessment} className="w-full bg-crimson hover:bg-crimson/90 text-white py-3 text-base" data-testid="start-assessment-btn">
              Start Assessment
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ─── RESULTS PHASE ───
  if (phase === "results" && results) {
    return (
      <div className="min-h-screen bg-obsidian text-white">
        <div className="max-w-3xl mx-auto p-6 sm:p-10 space-y-6">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-obsidian-light border border-[#222] rounded-lg p-6 sm:p-8 text-center space-y-4">
            {results.passed ? (
              <>
                <div className="w-20 h-20 bg-emerald-500/10 border-2 border-emerald-500/40 rounded-full flex items-center justify-center mx-auto">
                  <Trophy className="w-10 h-10 text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold text-emerald-400" data-testid="result-passed">Assessment Passed!</h2>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-amber-500/10 border-2 border-amber-500/40 rounded-full flex items-center justify-center mx-auto">
                  <Target className="w-10 h-10 text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold text-amber-400" data-testid="result-failed">Keep Practicing!</h2>
              </>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4">
              {[
                { label: "Your Score", value: `${results.score}%`, highlight: results.passed },
                { label: "Passing", value: `${results.passing_score}%` },
                { label: "Points", value: `${results.earned_points}/${results.total_points}` },
                { label: "Time", value: formatTime(results.time_taken) },
              ].map((m) => (
                <div key={m.label} className="bg-obsidian border border-[#333] rounded p-3">
                  <p className="text-xs text-gray-500">{m.label}</p>
                  <p className={`text-xl font-bold ${m.highlight ? "text-emerald-400" : ""}`}>{m.value}</p>
                </div>
              ))}
            </div>

            {results.badge_earned && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-4 flex items-center gap-3">
                <Award className="w-8 h-8 text-amber-400" />
                <div className="text-left">
                  <p className="font-semibold text-amber-400">Skill Badge Earned!</p>
                  <p className="text-sm text-gray-400">This badge is now visible on your profile</p>
                </div>
              </div>
            )}
          </motion.div>

          {/* Detailed Results */}
          <div className="bg-obsidian-light border border-[#222] rounded-lg p-6">
            <h3 className="font-semibold mb-4 text-lg">Question Breakdown</h3>
            <div className="space-y-3">
              {results.detailed_results?.map((r, idx) => (
                <div key={r.question_id} className={`border rounded p-4 ${r.is_correct ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`} data-testid={`result-q-${idx}`}>
                  <div className="flex items-start gap-3">
                    {r.is_correct ? (
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300">Q{idx + 1}: {questions[idx]?.question_text}</p>
                      <div className="mt-2 text-xs space-y-1">
                        <p>
                          <span className="text-gray-500">Your answer:</span>{" "}
                          <span className={r.is_correct ? "text-emerald-400" : "text-rose-400"}>{r.user_answer || "(no answer)"}</span>
                        </p>
                        {!r.is_correct && (
                          <p>
                            <span className="text-gray-500">Correct:</span>{" "}
                            <span className="text-emerald-400">{r.correct_answer}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0">{r.points_earned}/{r.points_possible} pts</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={() => navigate("/assessments")} variant="outline" className="flex-1 border-[#333] text-gray-300 hover:bg-white/5" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Browse More
            </Button>
            <Button onClick={() => { setPhase("intro"); setAnswers({}); setCurrentQ(0); setResults(null); setTimeLeft(assessment.time_limit * 60); }} className="flex-1 bg-crimson hover:bg-crimson/90 text-white" data-testid="retake-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Retake
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── TAKING PHASE ───
  const q = questions[currentQ];
  const timeWarning = timeLeft < 60;
  const timeCritical = timeLeft < 30;

  return (
    <div className="min-h-screen bg-obsidian text-white">
      {/* Timer Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-obsidian-light border-b border-[#222]">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <p className="text-sm font-medium truncate">{assessment.title}</p>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded font-mono text-sm font-bold ${timeCritical ? "bg-rose-500/20 text-rose-400 animate-pulse" : timeWarning ? "bg-amber-500/20 text-amber-400" : "bg-white/5 text-white"}`} data-testid="timer">
            <Clock className="w-4 h-4" />
            {formatTime(timeLeft)}
          </div>
        </div>
        {/* Progress */}
        <div className="h-1 bg-[#222]">
          <div className="h-full bg-crimson transition-all duration-300" style={{ width: `${((answeredCount) / questions.length) * 100}%` }} />
        </div>
      </div>

      <div className="max-w-4xl mx-auto pt-20 pb-10 px-4 sm:px-6">
        {/* Question nav dots */}
        <div className="flex flex-wrap gap-2 mb-6">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToQuestion(idx)}
              className={`w-8 h-8 rounded text-xs font-bold transition-colors ${
                idx === currentQ
                  ? "bg-crimson text-white"
                  : answers[questions[idx]?.question_id]
                  ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                  : "bg-obsidian-light border border-[#333] text-gray-400 hover:border-crimson/50"
              }`}
              data-testid={`q-nav-${idx}`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="bg-obsidian-light border border-[#222] rounded-lg p-6 sm:p-8 space-y-5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Question {currentQ + 1} of {questions.length}</span>
              <span className="text-xs text-gray-500">{q?.points || 10} points</span>
            </div>

            <h2 className="text-lg sm:text-xl font-semibold" data-testid="question-text">{q?.question_text}</h2>

            {/* Answer options */}
            {(q?.question_type === "mcq" || q?.question_type === "true_false") && (
              <div className="space-y-3">
                {q.options?.map((opt, oi) => (
                  <button
                    key={oi}
                    onClick={() => handleAnswer(q.question_id, opt)}
                    className={`w-full text-left p-4 rounded border transition-all text-sm ${
                      answers[q.question_id] === opt
                        ? "bg-crimson/10 border-crimson text-white"
                        : "bg-obsidian border-[#333] text-gray-300 hover:border-[#555] hover:bg-white/5"
                    }`}
                    data-testid={`option-${oi}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${answers[q.question_id] === opt ? "border-crimson bg-crimson" : "border-[#555]"}`}>
                        {answers[q.question_id] === opt && <div className="w-2 h-2 bg-white rounded-full" />}
                      </div>
                      {opt}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {q?.question_type === "short_answer" && (
              <input
                type="text"
                value={answers[q.question_id] || ""}
                onChange={(e) => handleAnswer(q.question_id, e.target.value)}
                placeholder="Type your answer..."
                className="w-full bg-obsidian border border-[#333] rounded px-4 py-3 text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
                data-testid="short-answer-input"
              />
            )}

            {q?.question_type === "coding" && (
              <div className="space-y-2">
                {q.code_template && (
                  <pre className="bg-obsidian border border-[#333] rounded p-4 text-sm text-gray-400 overflow-x-auto font-mono">
                    {q.code_template}
                  </pre>
                )}
                <textarea
                  value={answers[q.question_id] || ""}
                  onChange={(e) => handleAnswer(q.question_id, e.target.value)}
                  placeholder="Write your code here..."
                  rows={5}
                  className="w-full bg-obsidian border border-[#333] rounded px-4 py-3 text-white placeholder-gray-500 focus:border-crimson focus:outline-none font-mono text-sm"
                  data-testid="coding-answer-input"
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            onClick={() => goToQuestion(currentQ - 1)}
            disabled={currentQ === 0}
            variant="outline"
            className="border-[#333] text-gray-300 hover:bg-white/5 disabled:opacity-30"
            data-testid="prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>

          <span className="text-xs text-gray-500">{answeredCount}/{questions.length} answered</span>

          {currentQ < questions.length - 1 ? (
            <Button
              onClick={() => goToQuestion(currentQ + 1)}
              className="bg-crimson hover:bg-crimson/90 text-white"
              data-testid="next-btn"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={phase === "submitting"}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="submit-assessment-btn"
            >
              {phase === "submitting" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TakeAssessment;
